import { getSimpleValidationResolver, type ValidateForm } from 'react-admin';
import {
  type CriteriaMode,
  type Field,
  type FieldError,
  type FieldValues,
  get,
  type Resolver,
  type ResolverOptions,
  set,
} from 'react-hook-form';

/**
 * react-admin turns the `validate` prop of a form into a react-hook-form `resolver`
 * (see `useAugmentedForm`). react-hook-form runs *either* the resolver *or* the rules
 * registered by each field — never both — so as soon as a form declares `validate`,
 * every `validate` prop of its inputs is silently ignored.
 *
 * This resolver restores the missing half: it runs the form level validation *and*
 * the validators registered by the inputs, then merges both error sets.
 *
 * Prefer the `SimpleForm` of `@sh4res/bo-common/components`, which wires this up for
 * you, over calling this directly.
 */
export const composeValidationResolver = <TFieldValues extends FieldValues = FieldValues>(
  validate: ValidateForm,
): Resolver<TFieldValues> => {
  const validateFormValues = getSimpleValidationResolver(validate);

  return async (values, _context, options) => {
    const [formLevel, fieldLevel] = await Promise.all([
      validateFormValues(values),
      validateRegisteredFields(values, options.fields, options.criteriaMode),
    ]);

    const errors = { ...formLevel.errors };
    // Field level errors take precedence over form level ones: they carry the
    // basics (required, format), while form level validation usually carries
    // cross-field rules that make little sense on a field that is still empty.
    fieldLevel.forEach(([name, error]) => set(errors, name, error));

    return Object.keys(errors).length === 0
      ? { values, errors: {} }
      : { values: {}, errors: errors as Awaited<ReturnType<Resolver<TFieldValues>>>['errors'] };
  };
};

type RegisteredField = Field['_f'];

/**
 * `options.fields` is typed as a flat record but react-hook-form actually builds it
 * with `set()`, so nested sources (`a.b`, array inputs, ...) come as nested objects
 * and arrays. Walk the whole tree and keep the leaves.
 */
const collectRegisteredFields = (
  node: unknown,
  accumulator: RegisteredField[] = [],
): RegisteredField[] => {
  if (isRegisteredField(node)) {
    accumulator.push(node);
  } else if (typeof node === 'object' && node !== null) {
    Object.values(node).forEach((child) => collectRegisteredFields(child, accumulator));
  }
  return accumulator;
};

const isRegisteredField = (node: unknown): node is RegisteredField =>
  typeof node === 'object' &&
  node !== null &&
  'ref' in node &&
  typeof (node as RegisteredField).name === 'string';

const validateRegisteredFields = async (
  values: FieldValues,
  fields: ResolverOptions<FieldValues>['fields'],
  criteriaMode: CriteriaMode | undefined,
): Promise<[string, FieldError][]> => {
  const results = await Promise.all(
    collectRegisteredFields(fields).map(
      async (field) =>
        [field.name, await validateRegisteredField(field, values, criteriaMode)] as const,
    ),
  );

  return results.filter((result): result is [string, FieldError] => result[1] !== undefined);
};

const validateRegisteredField = async (
  field: RegisteredField,
  values: FieldValues,
  criteriaMode: CriteriaMode | undefined,
): Promise<FieldError | undefined> => {
  // Mirror react-hook-form, which skips unmounted and disabled fields.
  if (field.mount === false || field.disabled === true) {
    return undefined;
  }

  const value = get(values, field.name);
  const errors: FieldError[] = [];

  const requiredError = validateRequired(field, value);
  if (requiredError) {
    errors.push(requiredError);
  }

  // Skip the `validate` rules once `required` already failed, unless every error is wanted.
  if (errors.length === 0 || criteriaMode === 'all') {
    errors.push(...(await validateFieldValidators(field, value, values, criteriaMode)));
  }

  if (errors.length === 0) {
    return undefined;
  }

  const [firstError] = errors;
  if (criteriaMode !== 'all') {
    return firstError;
  }
  return {
    ...firstError,
    types: Object.fromEntries(errors.map(({ type, message }) => [type, message || true])),
  };
};

/**
 * Runs the `validate` rules registered on a field, mirroring react-hook-form's own
 * handling of a single validate function vs. a record of named validators.
 */
const validateFieldValidators = async (
  field: RegisteredField,
  value: unknown,
  values: FieldValues,
  criteriaMode: CriteriaMode | undefined,
): Promise<FieldError[]> => {
  if (!field.validate) {
    return [];
  }

  const validators =
    typeof field.validate === 'function' ? { validate: field.validate } : field.validate;
  const errors: FieldError[] = [];

  for (const [type, validator] of Object.entries(validators)) {
    // Sequential on purpose: under the default `firstError` criteriaMode the remaining
    // validators must not run once one of them failed.
    // oxlint-disable-next-line no-await-in-loop
    const error = toFieldError(await validator(value, values), field, type);
    if (error) {
      errors.push(error);
      if (criteriaMode !== 'all') {
        break;
      }
    }
  }

  return errors;
};

/**
 * Mirrors react-hook-form's own `required` rule, which is registered via a field's
 * `rules` (e.g. `useController`/`Controller`) rather than react-admin's `validate`
 * prop. `RadioButtonGroupInput` is a real example of this in the codebase.
 */
const validateRequired = (field: RegisteredField, value: unknown): FieldError | undefined => {
  const rule = field.required;
  if (rule === undefined || rule === false) {
    return undefined;
  }

  const isRuleObject = typeof rule === 'object';
  if (isRuleObject && !rule.value) {
    return undefined;
  }

  const isEmpty =
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0);
  if (!isEmpty) {
    return undefined;
  }

  const message = isRuleObject ? rule.message : rule;

  return {
    type: 'required',
    message: typeof message === 'string' ? message : '',
    ref: field.refs ? field.refs[0] : field.ref,
  };
};

/**
 * Same contract as react-hook-form's internal `getValidateError`: only a string,
 * an array of strings or `false` count as an error, anything else (`true`,
 * `undefined`, `null`) means the field is valid.
 */
const toFieldError = (
  result: unknown,
  field: RegisteredField,
  type: string,
): FieldError | undefined => {
  const isStringOrStringArray =
    typeof result === 'string' ||
    (Array.isArray(result) && result.every((item) => typeof item === 'string'));

  if (!isStringOrStringArray && result !== false) {
    return undefined;
  }

  return {
    type,
    message: typeof result === 'string' ? result : '',
    ref: field.refs ? field.refs[0] : field.ref,
  };
};
